import type {
  MediaBlock,
  MediaBlockValueType,
} from '@basehub/data/replicache/index'
import {
  allowsAnyFile,
  getAllowedMimeTypesArray,
} from '@basehub/data/replicache/schema/block/valid-mime-types'

export function getSafeMediaTypeByConstraints(
  type: string,
  constraints: MediaBlock['constraints']['allowedMimeTypes']
): MediaBlockValueType | null {
  if (constraints === null) return null
  if (getAllowedMimeTypesArray({ image: constraints.image }).includes(type)) {
    return 'image'
  }
  if (getAllowedMimeTypesArray({ video: constraints.video }).includes(type)) {
    return 'video'
  }
  if (getAllowedMimeTypesArray({ audio: constraints.audio }).includes(type)) {
    return 'audio'
  }
  if (
    getAllowedMimeTypesArray({ file: constraints.file }).includes(type) ||
    allowsAnyFile(constraints)
  ) {
    return 'file'
  }
  return null
}

export const getMediaTypeByFileExtension = (
  fileName: string
): MediaBlockValueType => {
  const fileExtension = fileName.split('.').pop()?.toLowerCase()
  if (!fileExtension) return 'file'

  if (
    [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'svg',
      'avif',
      'bmp',
      'tiff',
      'tif',
      'ico',
      'heic',
      'heif',
    ].includes(fileExtension)
  ) {
    return 'image'
  } else if (
    [
      'mp4',
      'webm',
      'mov',
      'avi',
      'ogv',
      'mkv',
      'flv',
      'm4v',
      '3gp',
      'mts',
      'm2ts',
    ].includes(fileExtension)
  ) {
    return 'video'
  } else if (
    [
      'mp3',
      'wav',
      'ogg',
      'oga',
      'm4a',
      'flac',
      'aac',
      'wma',
      'aiff',
      'aif',
    ].includes(fileExtension)
  ) {
    return 'audio'
  }

  return 'file'
}

export function arraysHaveSameItems(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) return false

  const sorted1 = [...arr1].sort()
  const sorted2 = [...arr2].sort()

  for (let i = 0; i < sorted1.length; i++) {
    if (sorted1[i] !== sorted2[i]) return false
  }

  return true
}

const formatCache = new Map<string, string>()

export const prettifyMimeType = (format: string): string => {
  const cached = formatCache.get(format)
  if (cached) return cached

  const strippedFormat = format.replace(/^\w+\/(x-)?|[+]xml$/g, '')

  let name = strippedFormat
  switch (strippedFormat) {
    case 'msvideo':
      name = 'avi'
      break
    case 'quicktime':
      name = 'mov'
      break
    case 'matroska':
      name = 'mkv'
      break
    case 'mpeg':
      name = 'mpeg'
      break
    case 'msword':
      name = 'doc'
      break
    case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
      name = 'docx'
      break
    case 'vnd.ms-excel':
      name = 'xls'
      break
    case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      name = 'xlsx'
      break
    case 'vnd.ms-powerpoint':
      name = 'ppt'
      break
    case 'vnd.openxmlformats-officedocument.presentationml.presentation':
      name = 'pptx'
      break
    case 'plain':
      name = 'txt'
      break
    case 'vnd.oasis.opendocument.text':
      name = 'odt'
      break
    default:
      name = strippedFormat.replace(/.*\./g, '')
      break
  }

  name = name.toUpperCase()

  // Cache the result
  formatCache.set(format, name)
  return name
}
